import { Container, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type { PickEmModel } from '../../model/pickem/PickEmModel';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface PickEmHudCallbacks {
  onReplay: () => void;
}

/**
 * Pick-Em HUD: balance counter, picks-remaining label above the grid,
 * status text + replay button below. Subscribes to the model so picks-
 * remaining + winnings update automatically.
 */
export class PickEmHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly picksLabel: Text;
  private readonly statusLabel: Text;
  private readonly replayButton: PrimaryButton;
  private readonly subscriptions: Disposable[] = [];

  constructor(
    private readonly model: PickEmModel,
    private readonly callbacks: PickEmHudCallbacks,
    /** External balance read for the BalanceDisplay; the pick-em model
     * itself doesn't carry a player balance — wins accumulate as a
     * round total. */
    private readonly initialBalance: number,
  ) {
    super();
    this.balance = new BalanceDisplay(this.initialBalance);
    this.picksLabel = this.buildText(`tap a box · ${this.model.picksAllowed} picks`, 14, 0xffffff);
    this.statusLabel = this.buildText('', 13, 0xffffff);
    this.statusLabel.alpha = 0.7;
    this.replayButton = new PrimaryButton({
      label: 'PLAY AGAIN',
      width: 200,
      fillActive: 0xff6bd6,
      fillHover: 0x6b3d77,
      textColor: 0xffffff,
      onTap: () => this.callbacks.onReplay(),
    });
    this.replayButton.visible = false;

    this.addChild(this.balance, this.picksLabel, this.statusLabel, this.replayButton);

    this.subscriptions.push(
      this.model.events.on('box-revealed', (event) => {
        const total = this.model.totalWinnings();
        this.balance.setAmount(this.initialBalance + total);
        this.balance.bump();
        this.statusLabel.text = `+${event.prize.value} ${event.prize.label ?? ''}`.trim();
        this.picksLabel.text =
          event.picksRemaining > 0
            ? `${event.picksRemaining} pick${event.picksRemaining === 1 ? '' : 's'} left`
            : 'final reveal';
      }),
      this.model.events.on('pick-em-complete', (event) => {
        this.picksLabel.text = `🏁 ${event.totalWinnings.toLocaleString()} won`;
        this.statusLabel.text = 'tap PLAY AGAIN for another round';
        this.replayButton.visible = true;
      }),
    );
  }

  layout(ctx: SceneLayoutContext): void {
    const { centerX, machineTop, machineBottom, spinButtonY } = ctx;
    this.balance.x = centerX;
    this.balance.y = Math.max(16, machineTop - 96);
    this.picksLabel.x = centerX;
    this.picksLabel.y = Math.max(this.balance.y + 56, machineTop - 24);
    this.statusLabel.x = centerX;
    this.statusLabel.y = machineBottom + 28;
    this.replayButton.x = centerX;
    this.replayButton.y = spinButtonY;
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  resetForNewRound(): void {
    this.replayButton.visible = false;
    this.statusLabel.text = '';
    this.picksLabel.text = `tap a box · ${this.model.picksAllowed} picks`;
  }

  private buildText(text: string, size: number, fill: number): Text {
    const t = new Text({
      text,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: size,
        fontWeight: '700',
        fill,
        align: 'center',
      },
    });
    t.anchor.set(0.5);
    return t;
  }
}
